/* eslint-disable no-console */
import { devSettings, isDev } from "@/dev";

interface HeatAuthService {
    validateToken(token: string): Promise<{
        userId: string;
        username: string;
        firstName: string;
        lastName: string;
        email: string;
        role: number;
    }>;
    refreshToken(refreshToken: string): Promise<{ accessToken: string }>;
    logout(): void;
    getAccessToken(): string | null;
    getRefreshToken(): string | null;
    getLegacyToken(): string | null;
}

class HeatAuthServiceImpl implements HeatAuthService {
    private baseUrl: string;

    constructor(baseUrl: string) {
        this.baseUrl = baseUrl;
    }

    async validateToken(token: string): Promise<{
        userId: string;
        username: string;
        firstName: string;
        lastName: string;
        email: string;
        role: number;
    }> {
        let tokenData = token;
        let url = "/api/authorization/validate";

        if (isDev) {
            tokenData = devSettings.api.heatToken;
            url = devSettings.api.url + url;
        }

        try {
            const response = await fetch(url, {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify({ token: tokenData }),
            });

            if (!response.ok) {
                this.logout();
                throw new Error("Invalid token");
            }

            const result = await response.json();

            (window as any).heatUserData = result;

            return result;
        } catch (error) {
            console.error("[HeatAuthService] validateToken error:", error);
            throw error;
        }
    }

    async refreshToken(refreshToken: string): Promise<{ accessToken: string }> {
        try {
            const response = await fetch(`/api/authorization/refresh`, {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify({ refreshToken }),
            });

            if (!response.ok) {
                this.logout();
                throw new Error("Invalid refresh token");
            }

            const data = await response.json();
            this.storeTokens(
                data.accessToken,
                refreshToken,
                localStorage.getItem("legacyToken") || "",
            );

            return { accessToken: data.accessToken };
        } catch (error) {
            console.error("[HeatAuthService] refreshToken error:", error);
            throw error;
        }
    }

    logout(force: boolean = false): void {
        console.log("[HeatAuthService] Logging out...");
        localStorage.removeItem("accessToken");
        localStorage.removeItem("refreshToken");
        localStorage.removeItem("legacyToken");

        const redirect = window.location.href;
        window.location.href = `/login?${force ? "force=true&" : ""}redirect=${encodeURIComponent(redirect)}`;
    }

    getAccessToken(): string | null {
        return localStorage.getItem("accessToken");
    }

    getRefreshToken(): string | null {
        return localStorage.getItem("refreshToken");
    }

    getLegacyToken(): string | null {
        return localStorage.getItem("legacyToken");
    }

    private storeTokens(accessToken: string, refreshToken: string, legacyToken: string): void {
        localStorage.setItem("accessToken", accessToken);
        localStorage.setItem("refreshToken", refreshToken);
        localStorage.setItem("legacyToken", legacyToken);
        // Optionally, set cookies if needed
        document.cookie = `accessToken=${accessToken}; path=/; Secure; SameSite=Strict`;
        document.cookie = `refreshToken=${refreshToken}; path=/; Secure; SameSite=Strict`;
        document.cookie = `legacyToken=${refreshToken}; path=/; Secure; SameSite=Strict`;
    }
}

export const heatAuthService = new HeatAuthServiceImpl(
    window.location.protocol + "/" + window.location.hostname + "/" || "http://localhost:3000",
);
